import { DISPLAY, DISPLAY_KEY } from './bgData';

/**
 * Kulčina's display: 15x32 cells, each 2x2px on a 3px pitch. One Uint8Array is
 * the single source of truth, rendered to two canvases that share the same
 * 44x95 backing store — the one on the skyline at native size, and the editor's
 * blown up with pixelated scaling. No second geometry to keep in sync.
 */
export const CELLS = DISPLAY.cols * DISPLAY.rows;

export function fromHex(hex: string | null): Uint8Array | null {
  if (typeof hex !== 'string' || hex.length < Math.ceil(CELLS / 8) * 2) return null;
  const bits = new Uint8Array(CELLS);
  for (let i = 0; i < CELLS; i += 1) {
    const byte = parseInt(hex.substr((i >> 3) * 2, 2), 16);
    if (Number.isNaN(byte)) return null;
    bits[i] = (byte >> (i & 7)) & 1;
  }
  return bits;
}

export function toHex(bits: Uint8Array): string {
  let out = '';
  for (let i = 0; i < CELLS; i += 8) {
    let v = 0;
    for (let k = 0; k < 8 && i + k < CELLS; k += 1) v |= bits[i + k]! << k;
    out += v.toString(16).padStart(2, '0');
  }
  return out;
}

/** Overwrite in place. Both canvases and the sync hook hold this same array, so
 * replacing it would leave one of them painting a grid nobody else can see. */
export function setFromHex(bits: Uint8Array, hex: string): boolean {
  const next = fromHex(hex);
  if (!next) return false;
  bits.set(next);
  return true;
}

/** Apply cell ops in order. The client twin of `applyKulcinaOps` on the server. */
export function applyOps(bits: Uint8Array, ops: readonly (readonly [number, 0 | 1])[]): void {
  for (const [index, on] of ops) {
    if (index >= 0 && index < CELLS) bits[index] = on;
  }
}

/**
 * localStorage is now a *cache of the server's last known state*, nothing more.
 * The drawing lives in Postgres; this exists so the tower shows the right thing
 * on the first frame instead of flashing the built-in default while GET
 * /api/kulcina is in flight.
 *
 * Storage can throw outright (private mode, blocked site data) and can read back
 * empty, so neither path is allowed to break drawing — a cold cache just means
 * one frame of the default drawing.
 */
export function loadCachedBits(): Uint8Array {
  try {
    return fromHex(localStorage.getItem(DISPLAY_KEY)) ?? fromHex(DISPLAY.fallback)!;
  } catch {
    return fromHex(DISPLAY.fallback)!;
  }
}

/** Called only with state the server has confirmed, never with a local stroke. */
export function cacheBits(bits: Uint8Array): void {
  try {
    localStorage.setItem(DISPLAY_KEY, toHex(bits));
  } catch {
    /* a cache that cannot be written is a cache that is always cold; harmless */
  }
}

/**
 * `showOff` paints the unlit cells at low alpha. The editor needs them (you
 * cannot aim at a grid you cannot see); the skyline must not have them, because
 * there they are simply the dark tower face.
 */
export function paint(ctx: CanvasRenderingContext2D, bits: Uint8Array, showOff: boolean): void {
  const { cols, pitch, cell, w, h, colour } = DISPLAY;
  ctx.clearRect(0, 0, w, h);
  for (let i = 0; i < CELLS; i += 1) {
    const on = bits[i] === 1;
    if (!on && !showOff) continue;
    ctx.fillStyle = on ? colour : 'rgba(223,126,26,.14)';
    ctx.fillRect((i % cols) * pitch, Math.floor(i / cols) * pitch, cell, cell);
  }
}
